import { Box, BoxProps } from '@mui/material'
import { BRAND_MARKS } from 'src/design-system/brand.generated'

// ----------------------------------------------------------------------
// The Apple mark. A third-party trademark, and not an icon: it cannot go
// through `Iconify`, which strokes a `fill: none` path, and its geometry is
// never altered.
//
// Apple draws its mark in one colour, the colour of whatever label it sits
// beside, so unlike Google's G it takes an ink rather than carrying its own.
// As with the Tesla mark, the default is the page's primary ink rather than
// whatever is being inherited, so a container authored for muted icons
// cannot hand it a grey; the button that draws it on Apple's fill says so.
//
// Used where the thing named is Apple: the Continue with Apple button, a
// linked Apple ID in Settings, and an Apple sign-in on the Managers screen.
// ----------------------------------------------------------------------

export interface AppleMarkProps
  extends Omit<
    BoxProps,
    'children' | 'width' | 'height' | 'dangerouslySetInnerHTML'
  > {
  /** Rendered size in px. The mark is square. */
  size?: number
  /**
   * What the mark says, when nothing beside it already says it. Left off it
   * is decorative and hidden, as it is inside a button reading Continue with
   * Apple.
   */
  title?: string
  /**
   * Take the ink of whatever is behind it, for a mark drawn on a fill that
   * states its own contrast: the leading mark on the Continue with Apple
   * button. Left off, the mark takes the page's primary ink.
   */
  inherit?: boolean
}

/**
 * Half a pixel under the size Google's G is drawn at beside a label
 * (`GOOGLE_MARK_INLINE`), so the two read as one size wherever they stand
 * together: the sign-in buttons and the rows of the Sign-in methods card. At
 * 16, then 14, it read as the lesser of the two; at the G's own 18 as the
 * larger, because the apple fills its box from the leaf to the base where the
 * G's round strokes leave its corners open ("Make apple icon like 0.5
 * smaller", 2026-10-01).
 */
export const APPLE_MARK_INLINE = 17.5

/**
 * How far the mark's ink sits below its box's centre, as a share of its size.
 * The leaf raises the box over the apple, so centred by its box the apple sat
 * low beside its words. Measured by filling the path and taking the centroid
 * of its ink: 14.12 down the 24 unit box, against a centre of 12. Google's G is
 * round, so its box and its ink share a centre and it needs none.
 */
export const APPLE_MARK_INK_OFFSET = 0.088

/** The lift that centres the mark on its ink, at any size. */
export const APPLE_MARK_LIFT = `translateY(-${(
  APPLE_MARK_INK_OFFSET * 100
).toFixed(1)}%)`

export default function AppleMark({
  size = APPLE_MARK_INLINE,
  inherit = false,
  title,
  sx,
  ...other
}: AppleMarkProps & {
  width?: never
  height?: never
  dangerouslySetInnerHTML?: never
}) {
  const mark = BRAND_MARKS['apple-mark']

  return (
    <Box
      component="svg"
      viewBox={mark.viewBox}
      width={size}
      height={size}
      fill="currentColor"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      // A brand mark fills its box edge to edge, where a line glyph stands in
      // from it, so a button gives it more room before the label.
      data-brand-mark=""
      sx={[
        {
          display: 'block',
          flex: '0 0 auto',
          ...(inherit ? {} : { color: 'text.primary' }),
          // Every place draws it centred beside something (a button's words,
          // a row's label), so it is always centred on its ink. The lift is a
          // transform: it moves the paint and not the box, so nothing beside
          // the mark moves with it.
          transform: APPLE_MARK_LIFT,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      dangerouslySetInnerHTML={{ __html: mark.body }}
      {...other}
    />
  )
}
